import { useMemo, useState } from 'react';
import { apiBaseOrigin } from '../../../shared/config/api-origin';
import type {
  Assessment,
  AssessmentAuth,
} from '../../capability-equipment/assessment/adapters/syn-assessment-adapter';
import { createSynExactMatchingReader } from '../../capability-equipment/assessment/adapters/syn-exact-matching-reader';
import type {
  EvaluationState,
  RegistryDocument,
  RequirementProjection,
} from '../knowledge/consumer/contract';
import { matchRequirement, type MatchingSource } from '../knowledge/consumer/exact-matching';
import { ScenarioKnowledgeChain } from './ScenarioKnowledgeChain';
import './syn-exact-matching.css';

/** Mounted inside the existing 05 session. No new login, write operation or fixture. */
export function SynExactMatching({ auth }: { auth: AssessmentAuth }) {
  const [source, setSource] = useState<MatchingSource | null>(null);
  const reader = useMemo(
    () => createSynExactMatchingReader(apiBaseOrigin(), auth),
    [auth.session, auth.csrf],
  );
  const port = useMemo(() => {
    let registry: RegistryDocument;
    return {
      registry: async () => {
        registry = await reader.knowledge.registry();
        return registry;
      },
      async requirements() {
        setSource(null);
        const document = await reader.knowledge.requirements();
        const snapshot = await reader.read(document, registry);
        setSource(snapshot);
        const requirements = document.requirements.map((row) => {
          const result = matchRequirement(row, snapshot);
          return {
            ...row,
            evaluationState: result.matchState,
            evaluationReason: result.reasonCode,
            evaluationEvidence: {
              availability: snapshot.status,
              conclusion: result.assessmentState,
              boundEvidenceCount: snapshot.assessments
                .filter((a) => result.exactReferences.assessmentIds.includes(a.assessmentId))
                .reduce((n, a) => n + a.evidence.length, 0),
              detail: 'S6 精确匹配：历史证据数量不代表机构资源覆盖完整。',
            },
          };
        });
        const evaluationCounts: Record<EvaluationState, number> = {
          CANDIDATE: 0,
          GAP: 0,
          UNKNOWN: 0,
          NOT_EVALUATED: 0,
        };
        requirements.forEach((r) => evaluationCounts[r.evaluationState]++);
        return { ...document, requirements, evaluationCounts };
      },
    };
  }, [reader]);
  return (
    <section className="s6-matching" aria-label="High-Rise S6 实验精确匹配">
      <h2>High-Rise · S6 实验能力匹配</h2>
      <p>
        读取当前会话机构的已发布装备与 S4 原始评估。场景尚未正式发布；SYN CANDIDATE 也不表示可派遣。
      </p>
      <ScenarioKnowledgeChain
        port={port}
        compact
        renderMatching={(row) =>
          source && (
            <MatchDetail key={row.requirementId} row={row} source={source} reader={reader} />
          )
        }
      />
    </section>
  );
}

function MatchDetail({
  row,
  source,
  reader,
}: {
  row: RequirementProjection;
  source: MatchingSource;
  reader: ReturnType<typeof createSynExactMatchingReader>;
}) {
  const result = matchRequirement(row, source);
  const [notice, setNotice] = useState('');
  async function evidence(record: Assessment, index: number) {
    const ref = record.evidence[index];
    if (!ref) return;
    setNotice('正在通过 S4 Owner 核对原始证据…');
    try {
      // Re-read the immutable identity before downloading its hash-checked bytes.
      const fresh = await reader.assessment.read(record.assessmentId);
      if (fresh.digest !== record.digest) throw new Error('ASSESSMENT_DIGEST_MISMATCH');
      const blob = await reader.assessment.evidence(record.assessmentId, ref);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = ref.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setNotice(`证据已核对 SHA-256：${ref.sha256}；仅为合成历史证据。`);
    } catch (error) {
      setNotice(`UNKNOWN：${error instanceof Error ? error.message : 'EVIDENCE_UNREADABLE'}`);
    }
  }
  return (
    <section className="scc-knowledge__chain" data-s6-match={row.requirementId}>
      <h3>4 · 需求 → 装备 → 评估证据 → S6 结论</h3>
      <p data-s6-result>
        映射 {result.mappingState} · 评估 {result.assessmentState} · 匹配 {result.matchState}
      </p>
      <p>{result.reasonCode}</p>
      <p>
        Owner 读取：{source.status} · {source.reason} · {source.observedAt}
      </p>
      <p>
        范围：{source.country} / {source.agency}；当前读取快照，重新读取后核对版本变化。
      </p>
      <p>
        需求 Definition：{row.capabilityDefinitionExactRef.capabilityId ?? '未映射'} / v
        {row.capabilityDefinitionExactRef.expectedDefinitionVersion ?? 'UNKNOWN'} /{' '}
        {row.capabilityDefinitionExactRef.expectedDefinitionDigest ?? 'UNKNOWN'}
      </p>
      <h4>05 当前已发布装备精确版本</h4>
      {source.publications.length === 0 && <p>无可确认装备；不据此推导 GAP。</p>}
      {source.publications.map((p) => (
        <article key={p.assetId} data-s6-asset={p.assetId}>
          <strong>
            {p.assetId} · v{p.publishedVersion}
          </strong>
          <p>{p.digest}</p>
          <p>
            {p.country} / {p.agency} · 发布于 {p.publishedAt}
          </p>
          <a href={`#s6-history-${p.assetId}`}>查看该装备的 S4 评估及原始证据</a>
        </article>
      ))}
      <h4>S4 Owner 原始评估（含不匹配与历史记录）</h4>
      <p>不同 Definition 身份仅作对照，不能按“流量”等名称等同。旧 v1 记录不适用于当前 v2。</p>
      {source.assessments.length === 0 && (
        <p>
          {source.status === 'AVAILABLE'
            ? 'Owner 本次返回无评估记录。'
            : 'S4 不可读取，不能宣称无评估。'}
        </p>
      )}
      {source.publications.map((p) => (
        <div id={`s6-history-${p.assetId}`} key={p.assetId} tabIndex={-1}>
          {source.assessments
            .filter((a) => a.asset.assetId === p.assetId)
            .map((a) => (
              <details key={a.assessmentId} data-s6-assessment={a.assessmentId}>
                <summary>
                  {a.asset.displayName} · 评估 v{a.revision} · {a.definition.capabilityId} ·{' '}
                  {a.applicability.status}
                </summary>
                <p>
                  Definition 身份：
                  {a.definition.capabilityId === row.capabilityDefinitionExactRef.capabilityId
                    ? '相同 ID；仍须核对版本/摘要'
                    : 'DIFFERENT_DEFINITION_ID'}
                </p>
                <p>
                  评估 {a.assessmentId} · {a.digest}
                </p>
                <p>
                  绑定装备 {a.asset.assetId} / v{a.asset.publishedVersion} / {a.asset.digest}
                </p>
                <p>
                  绑定 Definition {a.definition.capabilityId} / v{a.definition.definitionVersion} /{' '}
                  {a.definition.definitionDigest}
                </p>
                <p>
                  {a.decision?.outcome ?? 'UNDECIDED'} · {a.decision?.reason ?? '等待独立决定'} ·{' '}
                  {a.applicability.status} / {a.applicability.reason}
                </p>
                <p>
                  提交 {a.submittedAt} · 决定 {a.decision?.decidedAt ?? 'UNKNOWN'}
                </p>
                <p>方法：{a.method}</p>
                <p>原始观察：{a.observations}</p>
                <p>限制：{a.limitations}</p>
                {a.evidence.map((e, i) => (
                  <div key={e.id}>
                    <p>
                      {e.name} · {e.id} · {e.sha256} · {e.size} bytes
                    </p>
                    <p>
                      来源：{e.source} · 依据：{e.basis}
                    </p>
                    <button onClick={() => void evidence(a, i)}>核验并下载原始合成证据</button>
                  </div>
                ))}
              </details>
            ))}
        </div>
      ))}
      <p role="status">{notice}</p>
      <details>
        <summary>精确引用、限制与数据来源</summary>
        <pre>{JSON.stringify(result, null, 2)}</pre>
      </details>
    </section>
  );
}
